import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { callAPI } from '../../../utils/apiUtils';
import { apiUrls } from '../../../utils/apiUrls';
import { useSelector } from 'react-redux';

const WalletContext = createContext();

const fetchWalletStatus = async () => {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    return null; 
  }
  try {
    const response = await callAPI(apiUrls.walletGet, {}, 'GET', {});
    if (response?.data?.success) {
      const data = response.data.data;
      return {
        isLinked: data?.stripeOnboardingComplete || false,
        wallet: data?.wallet || {},
        user: {
          id: data?._id,
          fullName: data?.fullName,
          email: data?.email,
          phoneNumber: data?.phoneNumber,
          isCardSave: data?.isCardSave || false
        },
        raw: data
      };
    }
    throw new Error(
      response?.data?.message || 'Failed to fetch wallet status'
    );
  } catch (error) {
    return {
      isLinked: JSON.parse(
        localStorage.getItem('walletAccountLinked') || 'false'
      ),
      wallet: {},
      user: null
    };
  }
};

const getStripeRedirectUrl = async () => {
  const response = await callAPI(
    apiUrls.stripeRedirect,
    {},
    'GET',
    {}
  );
  if (response?.data?.success) {
    const redirectUrl =
      response?.data?.data?.accountLink?.url;
    if (redirectUrl) {
      return { redirectUrl };
    }
    throw new Error('No redirect URL received from Stripe');
  }
  throw new Error(
    response?.data?.message ||
    'Failed to get redirect URL'
  );
};

export const WalletProvider = ({ children }) => {
  const { user, isAuthenticated } = useSelector(
    (state) => state.auth
  );
  const [isWalletLinked, setIsWalletLinked] = useState(() => {
    if (!isAuthenticated || !user) return false;
    return JSON.parse(localStorage.getItem('walletAccountLinked') || 'false');
  });
  const {
    data: walletData,
    refetch: refetchWalletStatus,
    isLoading
  } = useQuery({
    queryKey: ['wallet', 'status', user?._id],
    queryFn: fetchWalletStatus,
    enabled: !!user && isAuthenticated,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
    retry: false,
    retryOnMount: false,
  });

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (walletData) {
      const linked = walletData?.isLinked || false;
      setIsWalletLinked(linked);
      localStorage.setItem(
        'walletAccountLinked',
        JSON.stringify(linked)
      );
      localStorage.setItem(
        'walletData',
        JSON.stringify(walletData)
      );
    }
  }, [walletData, isAuthenticated, user]);

  const { mutate: linkWallet, isPending: isRedirectLoading } = useMutation({
    mutationFn: getStripeRedirectUrl,
    onSuccess: (data) => {
      if (data?.redirectUrl) {
        localStorage.setItem('walletReturnPath', window.location.pathname);
        window.open(data.redirectUrl, '_blank');
      }
    },
    onError: (error) => {
      console.error('Failed to get redirect URL:', error);
    }
  });

  const updateWalletStatus = async (linked) => {
    if (!isAuthenticated || !user) return;
    setIsWalletLinked(linked);
    localStorage.setItem('walletAccountLinked', JSON.stringify(linked));
    await refetchWalletStatus();
  }

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setIsWalletLinked(false);
      localStorage.removeItem('walletAccountLinked');
      localStorage.removeItem('walletData');
    }
  }, [isAuthenticated, user]);

  const value = {
    isWalletLinked,
    walletData,
    linkWallet,
    refetchWalletStatus,
    updateWalletStatus,
    isRedirectLoading,
    isLoading,
    isAuthenticated,
    user
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error(
      'useWallet must be used within a WalletProvider'
    );
  }
  return context;
};